import { useMemo } from "react";

import { addDays, compareDay, isSameDay, startOfDay, startOfMonth } from "./dateUtils";

/** 캘린더 한 칸(하루)의 파생 상태. */
export interface CalendarDayCell {
  date: Date;
  /** viewDate와 같은 달인가(leading/trailing 날짜 구분용). */
  isCurrentMonth: boolean;
  isToday: boolean;
  /** min/max 또는 isDateDisabled로 비활성인가. */
  isDisabled: boolean;
}

export interface UseCalendarMonthParams {
  /** 표시 중인 달의 임의 날짜. */
  viewDate: Date;
  /** 주 시작 요일(0=일 … 6=토). */
  weekStartsOn: number;
  min?: Date;
  max?: Date;
  isDateDisabled?: (date: Date) => boolean;
}

/**
 * 표시 중인 달을 6주 × 7일 그리드로 만든다(항상 42칸 — 높이 흔들림 방지). 앞뒤로 인접 달
 * 날짜가 채워지며 `isCurrentMonth`로 구분한다. RFC-date-picker §Headless 엔진.
 */
export function useCalendarMonth({
  viewDate,
  weekStartsOn,
  min,
  max,
  isDateDisabled,
}: UseCalendarMonthParams): CalendarDayCell[][] {
  return useMemo(() => {
    const first = startOfMonth(viewDate);
    const leading = (first.getDay() - weekStartsOn + 7) % 7;
    const gridStart = addDays(first, -leading);
    const today = startOfDay(new Date());
    const viewMonth = viewDate.getMonth();

    const weeks: CalendarDayCell[][] = [];
    for (let w = 0; w < 6; w++) {
      const row: CalendarDayCell[] = [];
      for (let d = 0; d < 7; d++) {
        const date = addDays(gridStart, w * 7 + d);
        const belowMin = min != null && compareDay(date, min) < 0;
        const aboveMax = max != null && compareDay(date, max) > 0;
        row.push({
          date,
          isCurrentMonth: date.getMonth() === viewMonth,
          isToday: isSameDay(date, today),
          isDisabled: belowMin || aboveMax || (isDateDisabled?.(date) ?? false),
        });
      }
      weeks.push(row);
    }
    return weeks;
  }, [viewDate, weekStartsOn, min, max, isDateDisabled]);
}
